import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import { AIRPORTS, serviceNameFor, type Point, type SavedPlace } from '@rydres/shared';
import { AddressInput, Button, Card, Field, InfoTip, Input, Notice, PlaceButtons, PlacesEditor, TextArea, formatUsd, useAddressTarget, type PickedAddress } from '@rydres/ui';
import { driverApi, geoApi } from '../lib/api.js';
import { useProfile } from '../lib/profile.js';
import type { Service } from '../lib/types.js';

const airport = (code: string) => AIRPORTS.find((a) => a.code === code)!;
const EXAMPLES = [
  { name: 'LAX pickup → Orange County', priceCents: 14000, durationMin: 120, pickupAddress: airport('LAX').address, pickupPoint: airport('LAX').point },
  { name: 'SNA drop-off', priceCents: 7500, durationMin: 75, dropoffAddress: airport('SNA').address, dropoffPoint: airport('SNA').point },
  { name: 'Hourly as directed (3 hr minimum)', priceCents: 27000, durationMin: 180 },
];

export function Services() {
  const { profile, reload } = useProfile();
  const [editing, setEditing] = useState<Partial<Service> | null>(null);
  const [params, setParams] = useSearchParams();
  const list = profile?.services ?? [];
  // Today's "Add a service" lands here with ?new; a driver with no services sees the common routes instead.
  useEffect(() => {
    if (!profile || !params.has('new')) return;
    if (profile.services.length > 0) setEditing({});
    setParams({}, { replace: true });
  }, [profile, params, setParams]);
  const done = async () => {
    setEditing(null);
    await reload();
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-2xl font-black">
          Services
          <InfoTip label="Services">Fixed prices riders can book instantly. Every price is per vehicle and yours alone; the page never changes it.</InfoTip>
        </h1>
        <Button small onClick={() => setEditing({})}>+ Add</Button>
      </div>
      {list.length === 0 && !editing && (
        <Card title="Start with a common route">
          <div className="space-y-2">
            {EXAMPLES.map((e) => (
              <button key={e.name} onClick={() => setEditing({ ...e, maxPassengers: profile?.vehicleSeats ?? 4 })} className="flex w-full items-center justify-between rounded-xl bg-card2 px-3 py-2 text-left text-sm font-bold">
                {e.name} <span className="text-muted">{formatUsd(e.priceCents)}</span>
              </button>
            ))}
          </div>
        </Card>
      )}
      <div className="space-y-2">
        {list.map((s) => (
          <button key={s.id} onClick={() => setEditing(s)} className={`flex w-full items-start justify-between gap-3 rounded-2xl border border-line bg-card p-4 text-left ${s.active ? '' : 'opacity-50'}`}>
            <div>
              <div className="font-bold">{s.name}{!s.active && <span className="ml-2 text-xs text-muted">hidden</span>}</div>
              {s.description && <div className="text-sm text-ink2">{s.description}</div>}
              <div className="text-xs text-muted">{s.durationMin} min · up to {s.maxPassengers} pax</div>
            </div>
            <div className="text-lg font-black">{formatUsd(s.priceCents)}</div>
          </button>
        ))}
      </div>
      {profile && (
        <Card title="Your places" info="Saved addresses fill pickup and drop-off with one tap: in services, in Log a trip and, for the airports and places you show, on your booking page.">
          <PlacesEditor
            places={profile.places}
            pageToggle
            geo={geoApi}
            onSave={async (places) => {
              const r = await driverApi.savePlaces(places);
              void reload();
              return r.places;
            }}
          />
        </Card>
      )}
      {editing && <ServiceForm initial={editing} places={profile?.places ?? []} onDone={done} onCancel={() => setEditing(null)} />}
    </div>
  );
}

function ServiceForm({ initial, places, onDone, onCancel }: { initial: Partial<Service>; places: SavedPlace[]; onDone: () => void; onCancel: () => void }) {
  const address = useAddressTarget();
  const [f, setF] = useState({
    name: initial.name ?? '',
    description: initial.description ?? '',
    price: initial.priceCents != null ? String(initial.priceCents / 100) : '',
    durationMin: initial.durationMin ?? 90,
    maxPassengers: initial.maxPassengers ?? 4,
    active: initial.active ?? true,
    pickupAddress: initial.pickupAddress ?? '',
    dropoffAddress: initial.dropoffAddress ?? '',
    pickupPoint: initial.pickupPoint ?? (null as Point | null),
    dropoffPoint: initial.dropoffPoint ?? (null as Point | null),
  });
  // The name follows the two places until the driver types their own.
  const [ownName, setOwnName] = useState(Boolean(initial.name));
  const setPlace = (field: 'pickup' | 'dropoff', p: PickedAddress) =>
    setF((x) => {
      const next = { ...x, [`${field}Address`]: p.address, [`${field}Point`]: p.point };
      return ownName ? next : { ...next, name: serviceNameFor({ address: next.pickupAddress, point: next.pickupPoint }, { address: next.dropoffAddress, point: next.dropoffPoint }) ?? '' };
    });
  const pick = (value: string, point: Point | null) => {
    setPlace(address.target, { address: value, point });
    if (address.target === 'pickup' && !f.dropoffAddress) address.setTarget('dropoff');
  };
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = {
      name: f.name,
      description: f.description || null,
      priceCents: Math.round(Number(f.price) * 100),
      durationMin: Number(f.durationMin),
      maxPassengers: Number(f.maxPassengers),
      active: f.active,
      pickupAddress: f.pickupAddress.trim() || null,
      dropoffAddress: f.dropoffAddress.trim() || null,
      pickupPoint: f.pickupAddress.trim() ? f.pickupPoint : null,
      dropoffPoint: f.dropoffAddress.trim() ? f.dropoffPoint : null,
    };
    try {
      if (initial.id) await driverApi.updateService(initial.id, body);
      else await driverApi.addService(body);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save.');
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!initial.id) return;
    setBusy(true);
    await driverApi.removeService(initial.id).catch(() => undefined);
    onDone();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-scrim p-4 sm:items-center">
      <form onSubmit={submit} className="max-h-[92dvh] w-full max-w-md space-y-3 overflow-y-auto rounded-3xl border border-line bg-card p-5">
        <div className="text-lg font-black">{initial.id ? 'Edit service' : 'New service'}</div>
        <Field label="Name" hint="Filled in from the pickup and drop-off below. Change it if you like."><Input required value={f.name} onChange={(e) => { setOwnName(e.target.value.trim() !== ''); setF({ ...f, name: e.target.value }); }} placeholder="LAX Airport → Irvine" /></Field>
        <Field label="Description (optional)"><TextArea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Meet and greet at baggage claim, 60 min free wait" /></Field>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Price $"><Input type="number" step="0.01" min="0" required value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} /></Field>
          <Field label="Minutes" hint="blocks your calendar"><Input type="number" min={15} step={15} value={f.durationMin} onChange={(e) => setF({ ...f, durationMin: Number(e.target.value) })} /></Field>
          <Field label="Max pax"><Input type="number" min={1} max={14} value={f.maxPassengers} onChange={(e) => setF({ ...f, maxPassengers: Number(e.target.value) })} /></Field>
        </div>
        <Field label="Pickup (optional)" hint="Filled in for the rider when they choose this service">
          <AddressInput geo={geoApi} near={f.dropoffPoint} value={f.pickupAddress} onChange={(p) => setPlace('pickup', p)} onFocus={() => address.setTarget('pickup')} placeholder="Leave blank to let the rider type it" />
        </Field>
        <Field label="Drop-off (optional)">
          <AddressInput geo={geoApi} near={f.pickupPoint} value={f.dropoffAddress} onChange={(p) => setPlace('dropoff', p)} onFocus={() => address.setTarget('dropoff')} placeholder="SNA, a hotel, an address…" />
        </Field>
        <PlaceButtons places={places} target={address.target} onPick={pick} />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Shown on my page</label>
        {error && <Notice tone="danger">{error}</Notice>}
        <div className="flex gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} className="flex-1">Cancel</Button>
          <Button type="submit" disabled={busy} className="flex-1">Save This Service</Button>
        </div>
        {initial.id && <button type="button" onClick={() => void remove()} className="w-full text-sm text-danger">Delete this service</button>}
      </form>
    </div>
  );
}
